
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ProjectAudioElement {
  id: string;
  project_id: string;
  user_id: string;
  audio_type: string;
  name?: string;
  tts_script?: string;
  tts_voice_provider?: string;
  tts_voice_id?: string;
  tts_settings?: any;
  audio_storage_path?: string;
  duration_ms?: number;
  volume?: number;
  created_at: string;
  updated_at: string;
}

export const useProjectAudioElements = (projectId: string) => {
  return useQuery({
    queryKey: ['project-audio', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_audio_elements')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching project audio elements:', error);
        throw error;
      }

      return data as ProjectAudioElement[];
    },
    enabled: !!projectId,
  });
};

export const useCreateProjectAudio = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (audioData: {
      project_id: string;
      audio_type: string;
      name?: string;
      tts_script?: string;
      tts_voice_provider?: string;
      tts_voice_id?: string;
      tts_settings?: any;
      audio_storage_path?: string;
      duration_ms?: number;
      volume?: number;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('project_audio_elements')
        .insert([
          {
            ...audioData,
            user_id: user.id,
          },
        ])
        .select()
        .single();

      if (error) {
        console.error('Error creating project audio element:', error);
        throw error;
      }

      return data as ProjectAudioElement;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['project-audio', data.project_id] });
      toast.success('Audio element created successfully!');
    },
    onError: (error) => {
      console.error('Failed to create audio element:', error);
      toast.error('Failed to create audio element');
    },
  });
};
